import { Box, Button, Center, Flex, Text, VStack } from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';

import { usePostSmsVerifyConfirm } from '../../../services/marketplace/generated/kubb/hooks/usePostSmsVerifyConfirm';
import { usePostSmsVerifySend } from '../../../services/marketplace/generated/kubb/hooks/usePostSmsVerifySend';
import { formatPhoneInput } from '../../../utils/strings';
import { useLEAccordionItemContext } from '../../LEAccordion/LEAccordionItem/useLEAccordionItem';
import { Checkbox, PinPad } from '../../formComponents';
import PrimaryButton from '../../ui/buttons/PrimaryButton';
import useMarketplacePhoneConsent from './useMarketplacePhoneConsent';

const OTP_LENGTH = 6;
const RESEND_COOLDOWN_SECONDS = 30;

type CodeError = { message: string; blocking: boolean };

const WRONG_CODE_ERROR: CodeError = {
  message: "That code didn't match. Check the text and try again.",
  blocking: false,
};
const TOO_MANY_ERROR: CodeError = {
  message: 'Too many attempts. Resend a new code to try again.',
  blocking: true,
};
const SEND_FAILURE_ERROR: CodeError = {
  message: "We couldn't send a code. Please try again.",
  blocking: false,
};
const VERIFY_ERROR: CodeError = {
  message: 'Something went wrong verifying that code. Please try again.',
  blocking: false,
};
const CONSENT_SAVE_ERROR: CodeError = {
  message: "We couldn't record your consent. Please try again.",
  blocking: false,
};
const CONSENT_LOAD_ERROR = "We couldn't load the consent details. Refresh and try again.";

type VerifyNumberPanelProps = {
  phoneNumber: string;
  dealId?: string;
  verifiedNumber?: string;
  onEditNumber?: () => void;
  onVerified?: () => void;
  submitLabel?: string;
};

const VerifyNumberPanel = ({
  phoneNumber,
  dealId,
  verifiedNumber,
  onEditNumber,
  onVerified,
  submitLabel = 'VERIFY & CONTINUE',
}: VerifyNumberPanelProps) => {
  const { updateState, setOpenAccordionIndex, index } = useLEAccordionItemContext();
  const { consentCopy, consentLoading, saveConsent } = useMarketplacePhoneConsent(dealId);
  const { mutateAsync: sendCode } = usePostSmsVerifySend();
  const { mutateAsync: confirmCode } = usePostSmsVerifyConfirm();

  const isVerified = !!verifiedNumber && verifiedNumber === phoneNumber;

  const [code, setCode] = useState('');
  const [error, setError] = useState<CodeError | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(RESEND_COOLDOWN_SECONDS);
  const [hasSent, setHasSent] = useState(false);
  const [hasConsented, setHasConsented] = useState(false);
  const submittedCode = useRef<string | null>(null);
  const verifiedCode = useRef<string | null>(null);
  const lastSentNumber = useRef<string | null>(null);

  const consentSatisfied = !consentLoading && !!consentCopy && hasConsented;

  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const timer = setInterval(() => setResendCooldown((prev) => Math.max(0, prev - 1)), 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  const resetForFreshCode = () => {
    setCode('');
    submittedCode.current = null;
    verifiedCode.current = null;
    setError(null);
    setResendCooldown(RESEND_COOLDOWN_SECONDS);
  };

  useEffect(() => {
    if (isVerified) updateState({ status: 'complete' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVerified]);

  useEffect(() => {
    if (lastSentNumber.current !== null && lastSentNumber.current !== phoneNumber) {
      resetForFreshCode();
      setHasSent(false);
      setHasConsented(false);
      lastSentNumber.current = null;
    }
  }, [phoneNumber]);

  const handleSend = async () => {
    if (isSending) return;
    setIsSending(true);
    setError(null);
    lastSentNumber.current = phoneNumber;
    try {
      await sendCode({ data: { phone: phoneNumber } });
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
      setHasSent(true);
    } catch {
      lastSentNumber.current = null;
      setError(SEND_FAILURE_ERROR);
    } finally {
      setIsSending(false);
    }
  };

  const handleVerify = async () => {
    if (code.length !== OTP_LENGTH || isSubmitting || !consentSatisfied) return;
    // The code is consumed once confirmed; on a consent-save retry, skip re-confirming.
    const alreadyVerified = verifiedCode.current === code;
    if (!alreadyVerified && submittedCode.current === code) return;
    submittedCode.current = code;
    setIsSubmitting(true);
    setError(null);
    try {
      if (!alreadyVerified) {
        const { verified } = await confirmCode({ data: { phone: phoneNumber, code } });
        if (!verified) {
          // A wrong, expired, or consumed code all come back as verified:false.
          setError(WRONG_CODE_ERROR);
          setCode('');
          submittedCode.current = null;
          return;
        }
        verifiedCode.current = code;
      }
      try {
        await saveConsent();
        updateState({ status: 'complete' });
        onVerified?.();
        setOpenAccordionIndex(index + 1);
      } catch {
        setError(CONSENT_SAVE_ERROR);
        submittedCode.current = null;
      }
    } catch (caught) {
      // 429 is the marketplace IP rate limit.
      setError(
        (caught as { status?: number } | null)?.status === 429 ? TOO_MANY_ERROR : VERIFY_ERROR,
      );
      setCode('');
      submittedCode.current = null;
    } finally {
      setIsSubmitting(false);
    }
  };

  const canResend = resendCooldown <= 0 && !isResending;

  const handleResend = async () => {
    if (!canResend) return;
    setIsResending(true);
    setError(null);
    try {
      await sendCode({ data: { phone: phoneNumber } });
      resetForFreshCode();
    } catch {
      setError(SEND_FAILURE_ERROR);
    } finally {
      setIsResending(false);
    }
  };

  if (isVerified) {
    return (
      <VStack spacing={2}>
        <Text fontSize="lg" fontWeight="semibold" color="leaseEndGreen">
          Your number is verified
        </Text>
        <Text fontWeight="bold">{formatPhoneInput(phoneNumber, '(999) 999-9999')}</Text>
      </VStack>
    );
  }

  if (!hasSent) {
    return (
      <VStack spacing={4}>
        <Text fontSize="lg" fontWeight="semibold" color="leaseEndBlue" textAlign="center">
          We'll text a verification code to{' '}
          <Text as="span" fontWeight="bold">
            {formatPhoneInput(phoneNumber, '(999) 999-9999')}
          </Text>
        </Text>

        {error && (
          <Text fontSize="sm" color="leaseEndRed" textAlign="center">
            {error.message}
          </Text>
        )}

        <Center w="100%">
          <PrimaryButton onClick={handleSend} loading={isSending} value="SEND CODE" />
        </Center>

        {onEditNumber && (
          <Button variant="link" onClick={onEditNumber}>
            Edit number
          </Button>
        )}
      </VStack>
    );
  }

  const isInputDisabled = isSubmitting || !!error?.blocking;

  return (
    <VStack spacing={4}>
      <Flex align="center" justify="center" gap={2} flexWrap="wrap">
        <Text fontSize="lg" fontWeight="semibold" color="leaseEndBlue">
          We texted a code to{' '}
          <Text as="span" fontWeight="bold">
            {formatPhoneInput(phoneNumber, '(999) 999-9999')}
          </Text>
        </Text>
        {onEditNumber && (
          <Button variant="link" onClick={onEditNumber}>
            Edit number
          </Button>
        )}
      </Flex>

      <PinPad value={code} onChange={setCode} length={OTP_LENGTH} isDisabled={isInputDisabled} />

      {error && (
        <Text fontSize="sm" color="leaseEndRed" textAlign="center">
          {error.message}
        </Text>
      )}

      {consentCopy && (
        <Box maxW="450px">
          <Checkbox
            checked={hasConsented}
            onChange={(event) => setHasConsented(event.target.checked)}
            label={consentCopy}
          />
        </Box>
      )}

      {!consentLoading && !consentCopy && (
        <Text fontSize="sm" color="leaseEndRed" textAlign="center">
          {CONSENT_LOAD_ERROR}
        </Text>
      )}

      <Center w="100%">
        <PrimaryButton
          onClick={handleVerify}
          loading={isSubmitting}
          isDisabled={code.length !== OTP_LENGTH || isInputDisabled || !consentSatisfied}
          value={submitLabel}
        />
      </Center>

      <Text fontSize="sm" color="grayLight" textAlign="center">
        Didn't get it?{' '}
        <Button variant="link" onClick={handleResend} isDisabled={!canResend}>
          {resendCooldown > 0 ? `Resend code (${resendCooldown}s)` : 'Resend code'}
        </Button>
      </Text>
    </VStack>
  );
};

export default VerifyNumberPanel;
